import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Inventory } from "types/inventory";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { RevivnApiForm } from "components/Form/RevivnApiForm";
import { SubmitAwareButton } from "components/Form/SubmitAwareButton";
import DirectUploadField from "components/Form/DirectUploadField";
import { SaveAndMoveDialog } from "components/TechnicianInventory/v2/SaveAndMoveDialog";
import { useAlert } from "hooks/useAlert";

const photoOrder = [
  "hero",
  "keyboard",
  "screen_face",
  "front_edge",
  "front_face",
  "right_edge",
  "right_face",
  "left_edge",
  "left_face",
  "top_edge",
  "top_face",
  "top_angle",
  "rear_face",
  "bottom_edge",
  "bottom_face",
  "back_edge",
];

export function ReviewPhotosPage() {
  const { sessionPhotos, inventory } = useOutletContext<{ sessionPhotos: File[]; inventory: Inventory }>();
  const navigate = useNavigate();
  const orderedPhotos = useMemo(
    () => photoOrder.map((key) => sessionPhotos.find((f) => f.name.includes(key))).filter((x): x is File => !!x),
    [sessionPhotos],
  );

  const { alertSuccess } = useAlert();
  const photosSaved = useRef(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const returnToScan = () => setFinished(true);

  useEffect(() => {
    if (!finished) return;
    navigate("/resale-photo-booth", { replace: true });
  }, [finished, navigate]);

  if (sessionPhotos.length === 0) {
    navigate("/resale-photo-booth", { replace: true });
    return null;
  }

  const ReviewActions = () => {
    const { submitForm } = useFormikContext();

    const savePhotos = async () => {
      if (!photosSaved.current) await submitForm();
      return photosSaved.current;
    };

    const handleSave = async () => {
      if (await savePhotos()) returnToScan();
    };

    const handleSaveAndMove = async () => {
      if (await savePhotos()) setMoveDialogOpen(true);
    };

    return (
      <Stack direction="row" spacing={2} justifyContent="end">
        <Button component={Link} to=".." color="error" variant="text" reloadDocument>
          Restart
        </Button>
        <SubmitAwareButton
          type="button"
          color="primary"
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
        >
          Save
        </SubmitAwareButton>
        <SubmitAwareButton
          type="button"
          color="primary"
          variant="contained"
          onClick={() => {
            void handleSaveAndMove();
          }}
        >
          Save & Move
        </SubmitAwareButton>
      </Stack>
    );
  };

  return (
    <Box component={Paper} variant={"outlined"} padding={4}>
      <RevivnApiForm
        initialValues={{ inventory: { skip_state: true, skip_serial_number: true, skip_user_warehouse: true } }}
        action={`inventories/${inventory.id}`}
        method="PATCH"
        onSuccess={() => {
          photosSaved.current = true;
          alertSuccess("Photos uploaded successfully");
        }}
      >
        <Stack direction="column" spacing={4}>
          <Stack direction="column">
            <Typography fontSize="24px" fontWeight={600}>
              Review Photos
            </Typography>
            <Typography fontSize="14px" fontWeight={400}>
              Review all photos carefully, ensuring they are high quality, the laptop is clearly visible in all photos,
              photos are not blurry, and there are no blemishes or marks.
            </Typography>
          </Stack>
          <DirectUploadField
            reorderable={false}
            keyPrefix={`inventories/${inventory.id}/resale_photos/`}
            sx={{ ".dropzone": { display: "none" }, ".deleteAction": { display: "none" } }}
            name="inventory[resalePhotos]"
            service="amazon_public"
            accept={["image/jpeg"]}
            initialFiles={orderedPhotos}
            multiple
          />
          <Divider sx={{ my: 4 }} />

          <ReviewActions />
        </Stack>
      </RevivnApiForm>
      <SaveAndMoveDialog
        open={moveDialogOpen}
        inventoryId={inventory.id}
        onClose={() => setMoveDialogOpen(false)}
        onMoved={() => {
          setMoveDialogOpen(false);
          returnToScan();
        }}
      />
    </Box>
  );
}
